import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
interface TransactionsSectionProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
// Mock transaction data
const mockTransactions = {
  main: [{
    time: '12/3/25 1:04 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000003 TRX',
    fee: '0.000000 TRX',
    refId: 'FTPDTm4'
  }, {
    time: '12/3/25 1:04 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000001 TRX',
    fee: '0.000000 TRX',
    refId: 'FTSwpyv'
  }, {
    time: '12/3/25 1:03 PM',
    asset: 'TRON',
    ticker: 'TRX',
    type: 'Deposit',
    status: 'Failed',
    amount: '0.000003 TRX',
    fee: '0.000000 TRX',
    refId: 'FTGwNcn'
  }, {
    time: '12/3/25 1:00 PM',
    asset: 'Tether USD',
    ticker: 'USDT',
    type: 'Convert',
    status: 'Success',
    amount: '16.86370000 USDT',
    fee: '0.0000 USDT',
    refId: 'FTKmGjD'
  }],
  futures: [],
  'inverse-futures': [],
  earn: []
};
export function TransactionsSection({
  accountType
}: TransactionsSectionProps) {
  const [activeTab, setActiveTab] = useState<'transactions' | 'funding' | 'ledger'>('transactions');
  const transactions = mockTransactions[accountType] || [];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success':
        return <CheckCircle size={16} className="text-success" data-id="element-1824" />;
      case 'Failed':
        return <XCircle size={16} className="text-danger" data-id="element-1825" />;
      case 'Pending':
        return <Clock size={16} className="text-warning" data-id="element-1826" />;
      default:
        return <AlertCircle size={16} className="text-text-tertiary" data-id="element-1827" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Success':
        return 'text-success';
      case 'Failed':
        return 'text-danger';
      case 'Pending':
        return 'text-warning';
      default:
        return 'text-text-tertiary';
    }
  };
  return <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-1828">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle" data-id="element-1829">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-1830">
          <div className="flex items-center gap-1" data-id="element-1831">
            <button onClick={() => setActiveTab('transactions')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'transactions' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1832">
              Transactions
            </button>
            {accountType === 'main' && <>
                <button onClick={() => setActiveTab('funding')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'funding' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1833">
                  Funding
                </button>
                <button onClick={() => setActiveTab('ledger')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'ledger' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1834">
                  Ledger
                </button>
              </>}
          </div>
        </div>
      </div>

      {/* Transactions Content */}
      {transactions.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-1835">
          <div className="w-16 h-16 rounded-full bg-background-elevated flex items-center justify-center mb-4" data-id="element-1836">
            <Clock size={32} className="text-text-tertiary" data-id="element-1837" />
          </div>
          <p className="text-text-secondary text-sm" data-id="element-1838">No recent activity.</p>
        </div> : <>
          {/* Mobile Cards View */}
          <div className="md:hidden p-4 space-y-4" data-id="element-1839">
            {transactions.map((tx, idx) => <div key={idx} className="bg-background-elevated rounded-xl p-4 space-y-3" data-id="element-1840">
                <div className="flex justify-between items-start" data-id="element-1841">
                  <div data-id="element-1842">
                    <div className="font-medium text-text-primary text-sm" data-id="element-1843">
                      {tx.asset}
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-id="element-1844">
                      <span className="text-xs text-text-secondary" data-id="element-1845">
                        {tx.ticker}
                      </span>
                      <span className="px-1.5 py-0.5 bg-background-secondary text-text-secondary text-[10px] font-medium rounded" data-id="element-1846">
                        {tx.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-1847">
                    {getStatusIcon(tx.status)}
                    <span className={`text-sm font-medium ${getStatusColor(tx.status)}`} data-id="element-1848">
                      {tx.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1849">
                  <div data-id="element-1850">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1851">
                      Amount
                    </div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1852">
                      {tx.amount}
                    </div>
                  </div>
                  <div data-id="element-1853">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1854">Fee</div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1855">
                      {tx.fee}
                    </div>
                  </div>
                  <div data-id="element-1856">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1857">Time</div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1858">
                      {tx.time}
                    </div>
                  </div>
                  <div data-id="element-1859">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1860">
                      Ref ID
                    </div>
                    <div className="font-mono text-sm text-text-secondary" data-id="element-1861">
                      {tx.refId}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" data-id="element-1862">
            <table className="w-full min-w-[600px]" data-id="element-1863">
              <thead className="bg-background-elevated/50" data-id="element-1864">
                <tr className="border-b border-border-subtle" data-id="element-1865">
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1866">
                    Time
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1867">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1868">
                    Type
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1869">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1870">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1871">
                    Fee
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1872">
                    Ref ID
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1873">
                {transactions.map((tx, idx) => <tr key={idx} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1874">
                    <td className="px-6 py-4" data-id="element-1875">
                      <div className="text-sm text-text-primary font-numbers" data-id="element-1876">
                        {tx.time}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1877">
                      <div className="text-sm text-text-primary" data-id="element-1878">
                        {tx.asset}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-1879">
                        {tx.ticker}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1880">
                      <div className="text-sm text-text-primary" data-id="element-1881">{tx.type}</div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1882">
                      <div className="flex items-center gap-2" data-id="element-1883">
                        {getStatusIcon(tx.status)}
                        <span className={`text-sm font-medium ${getStatusColor(tx.status)}`} data-id="element-1884">
                          {tx.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1885">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1886">
                        {tx.amount}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1887">
                      <div className="text-sm font-numbers text-text-primary" data-id="element-1888">
                        {tx.fee}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1889">
                      <div className="text-sm font-mono text-text-secondary" data-id="element-1890">
                        {tx.refId}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}